<template>
    <div class="facility-management-info">
        <div class="info-container">
            <!-- 좌측: 메타데이터 입력/수정 폼 -->
            <div class="metadata-section">
                <h5 class="section-title">계측기 기본 정보</h5>
                <div class="form-container">
                    <div class="facility-form-row">
                        <div class="form-group">
                            <label class="form-label">제조사</label>
                            <b-form-input
                                v-model="metadata.manufacturer"
                                type="text"
                                class="form-control"
                                placeholder="제조사명을 입력하세요"
                            />
                        </div>
                        <div class="form-group">
                            <label class="form-label">모델명</label>
                            <b-form-input
                                v-model="metadata.modelNumber"
                                type="text"
                                class="form-control"
                                placeholder="모델명을 입력하세요"
                            />
                        </div>
                    </div>

                    <div class="facility-form-row">
                        <div class="form-group">
                            <label class="form-label">시리얼 넘버</label>
                            <b-form-input
                                v-model="metadata.serialNumber"
                                type="text"
                                class="form-control"
                                placeholder="시리얼 넘버를 입력하세요"
                            />
                        </div>
                        <div class="form-group">
                            <label class="form-label">설치장소</label>
                            <b-form-input
                                v-model="metadata.location"
                                type="text"
                                class="form-control"
                                placeholder="설치장소를 입력하세요"
                            />
                        </div>
                    </div>

                    <div class="facility-form-row">
                        <div class="form-group">
                            <label class="form-label">정격용량</label>
                            <div class="capacity-input-group">
                                <b-form-input
                                    v-model="metadata.ratedCapacity"
                                    type="text"
                                    class="form-control capacity-input"
                                    placeholder="0.00"
                                />
                                <b-form-input
                                    v-model="metadata.capacityUnit"
                                    type="text"
                                    class="form-control unit-input"
                                    placeholder="단위 (예: kW)"
                                />
                            </div>
                        </div>
                        <div class="form-group">
                            <label class="form-label">정격 COP</label>
                            <b-form-input
                                v-model="metadata.ratedCOP"
                                type="text"
                                class="form-control"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="form-label">설명</label>
                        <b-form-textarea
                            v-model="metadata.description"
                            class="form-control description-textarea"
                            placeholder="설명을 입력하세요"
                            no-auto-shrink
                        />
                    </div>

                    <!-- 검교정 이력 테이블 -->
                    <div class="form-group">
                        <label class="form-label">유지보수 이력</label>
                        <div class="facility-clbrtn-hist-table">
                            <TableList
                                :title="'유지보수 이력'"
                                :columns="clbrtnColumns"
                                :rows="clbrtnHist"
                                :keyField="'clbrtnIdx'"
                                :useFilter="false"
                                :fixedHeader="true"
                                :useMaxHeight="true"
                                :customMaxHeight="'190px'"
                                :mode="'view'"
                            />
                        </div>
                    </div>

                    <div class="form-actions">
                        <span v-if="metadata && metadata.updDt" class="update-date">
                            최종 수정: {{ formatDate(metadata.updDt) }}
                        </span>
                        <button @click="saveMetadata" class="btn btn-primary"><i class="fa fa-save"></i> 저장</button>
                    </div>
                </div>
            </div>

            <!-- 우측: 이미지 업로드 섹션 -->
            <div class="image-section">
                <div class="image-upload-container">
                    <div class="image-grid">
                        <div v-for="(image, index) in images" :key="index" class="image-item">
                            <div class="image-preview" @click="openImageInNewWindow(image)">
                                <img :src="image.url" :alt="`이미지 ${index + 1}`" class="preview-img" />
                                <div class="image-overlay">
                                    <button @click.stop="removeImage(index)" class="remove-btn" title="이미지 삭제">
                                        <i class="fa fa-times"></i>
                                    </button>
                                </div>
                                <div class="image-info-overlay">
                                    <span class="image-name">{{ image.name }}</span>
                                    <span class="image-size">{{ formatFileSize(image.size) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- 이미지 추가 버튼 -->
                        <div v-if="images.length < 3" class="image-upload-btn" @click="triggerFileUpload">
                            <div class="upload-icon">
                                <i class="fa fa-plus"></i>
                            </div>
                            <span class="upload-text">이미지 추가</span>
                            <span class="upload-hint">최대 3장</span>
                        </div>
                    </div>

                    <!-- 숨겨진 파일 입력 -->
                    <input
                        ref="fileInput"
                        type="file"
                        accept="image/*"
                        multiple
                        @change="handleFileUpload"
                        style="display: none"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import TableList from "@views/component/v2.1/ListDetailView/TableList.vue";

export default {
    props: {
        facilityIdx: {
            type: [String, Number],
            default: null,
        },
    },
    components: { TableList },
    data() {
        return {
            metadata: {},
            images: [],
            imageFields: ["instImage1", "instImage2", "instImage3"],
            clbrtnHist: [],
            clbrtnColumns: [
                { field: "processDate", label: "처리일자" },
                { field: "clbrnTypeCodeName", label: "유형" },
                { field: "clbrnOrg", label: "업체" },
                { field: "clbrnWorker", label: "담당자" },
                { field: "clbrnDescrb", label: "상세 내역" },
            ],
        };
    },

    async mounted() {
        await this.loadInitialData();
        await this.loadClbrtnHist();
    },

    beforeDestroy() {
        // Blob URL 정리 (Base64 URL은 해제할 필요 없음)
        this.images.forEach((image) => {
            if (image.blob && image.url && image.url.startsWith("blob:")) {
                URL.revokeObjectURL(image.url);
            }
        });
    },

    methods: {
        async loadClbrtnHist() {
            if (!this.facilityIdx) {
                return;
            }
            const response = await backEndApi.clbrtnHist.getClbrnHistListByFacility(this.facilityIdx);
            if (response.status !== 200) {
                throw new Error(response.data.message);
            }
            this.clbrtnHist = Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];
        },

        processMetadata(response) {
            return {
                facilityIdx: this.facilityIdx,
                capacityUnit: response.capacityUnit,
                location: response.location,
                manufacturer: response.manufacturer,
                modelNumber: response.modelNumber,
                ratedCOP: response.ratedCOP,
                ratedCapacity: response.ratedCapacity,
                serialNumber: response.serialNumber,
                description: response.description,
            };
        },
        async getFacilityOpInfo() {
            try {
                const response = await backEndApi.facility.getFacilityOpInfo(this.facilityIdx);

                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }

                return response.data;
            } catch (error) {
                this.alertWarning(error);
                console.error("getFacilityOpInfo 오류:", error);
                throw error;
            }
        },
        async upsertFacilityOpInfo() {
            try {
                const response = await backEndApi.facility.upsertFacilityOpInfo(this.processMetadata(this.metadata));

                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }

                this.alertNoti("저장이 완료되었습니다.");
                return response.data;
            } catch (error) {
                this.alertWarning(error);
                console.error("upsertFacilityOpInfo 오류:", error);
                throw error;
            }
        },
        async deleteFacilityOpInfo() {
            try {
                const response = await backEndApi.facility.deleteFacilityOpInfo(this.facilityIdx);

                this.alertNoti("삭제가 완료되었습니다.");

                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }

                return response.data;
            } catch (error) {
                this.alertWarning(error);
                console.error("deleteFacilityOpInfo 오류:", error);
                throw error;
            }
        },

        // Blob을 Base64로 변환하는 메서드
        blobToBase64(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        },

        // CSP 문제 해결을 위한 이미지 URL 생성
        async createImageUrl(blob) {
            try {
                const base64 = await this.blobToBase64(blob);
                return base64;
            } catch (error) {
                console.error("Base64 변환 실패:", error);
                return URL.createObjectURL(blob);
            }
        },
        // 썸네일 이미지 가져오기
        async getThumbnailImage(imageName, thumbnailName) {
            const response = await this.getFacilityOpInfoImage(thumbnailName);
            return response;
        },

        // 실제 원본 이미지 가져오기
        async getOriginalImage(imageName) {
            const response = await this.getFacilityOpInfoImage(imageName);
            return response;
        },

        // 이미지 가져오기 API 호출 함수
        async getFacilityOpInfoImage(imageName) {
            try {
                const response = await backEndApi.facility.getFacilityOpInfoImage(this.facilityIdx, imageName);

                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }

                // CSP 문제 해결을 위해 Base64로 변환하여 URL 생성
                const imageUrl = await this.createImageUrl(response.data);

                return {
                    url: imageUrl,
                    blob: response.data,
                };
            } catch (error) {
                this.alertWarning(error);
                console.error("getFacilityOpInfoImage 오류:", error);
                throw error;
            }
        },
        async uploadFacilityOpInfoImage(position, fileName, file) {
            try {
                const response = await backEndApi.facility.uploadFacilityOpInfoImage(
                    this.facilityIdx,
                    position,
                    fileName,
                    file
                );
                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }

                this.alertNoti("이미지 업로드가 완료되었습니다.");
                return response.data;
            } catch (error) {
                this.alertWarning(error);
                console.error("uploadFacilityOpInfoImage 오류:", JSON.stringify(error));
                throw error;
            }
        },
        async deleteFacilityOpInfoImage(position) {
            try {
                const response = await backEndApi.facility.deleteFacilityOpInfoImage(this.facilityIdx, position);
                if (response.status !== 200) {
                    throw new Error(response.data.message);
                }
                this.alertNoti("이미지 삭제가 완료되었습니다.");
                return response.data;
            } catch (error) {
                this.alertWarning(error);
                console.error("deleteFacilityOpInfoImage 오류:", error);
                throw error;
            }
        },

        async loadInitialData() {
            if (!this.facilityIdx) {
                return;
            }

            // API에서 데이터 로드
            const response = await this.getFacilityOpInfo();
            if (response) {
                this.metadata = response;
            }

            // 이미지 데이터 처리
            await this.loadFacilityImages(response);
        },

        async loadFacilityImages(metadata) {
            this.images = [];

            for (let i = 0; i < this.imageFields.length; i++) {
                const imageName = metadata[this.imageFields[i]];
                if (imageName) {
                    const imageData = await this.processImageData(imageName, i, `thumbnail-${i + 1}.jpg`);
                    if (imageData) {
                        this.images.push(imageData);
                    }
                }
            }
        },
        splitImageName(imageName) {
            const name = imageName.split(".")[0];
            return name;
        },

        async processImageData(imageName, index, thumbnailName) {
            try {
                const imageResponse = await this.getThumbnailImage(imageName, thumbnailName);
                if (imageResponse && imageResponse.url) {
                    return {
                        name: imageName,
                        size: imageResponse.blob ? imageResponse.blob.size : 0,
                        url: imageResponse.url,
                        blob: imageResponse.blob,
                        position: index + 1,
                    };
                }
            } catch (error) {
                console.warn(`이미지 로드 실패 (${imageName}):`, error);
            }
        },

        async saveMetadata() {
            await this.upsertFacilityOpInfo();
            await this.loadInitialData();
        },

        triggerFileUpload() {
            this.$refs.fileInput.click();
        },
        getRemainingSlots() {
            if (!this.metadata.instImage1) {
                return 1;
            }
            if (!this.metadata.instImage2) {
                return 2;
            }
            if (!this.metadata.instImage3) {
                return 3;
            }
            return false;
        },
        async handleFileUpload(event) {
            const files = Array.from(event.target.files)[0];

            // PNG 타입 이미지만 허용
            // if (files.type === "image/png") {
            // 이미지 업로드 즉시 API 호출
            const fileName = files.name;
            const remainingSlots = this.getRemainingSlots();

            try {
                await this.uploadFacilityOpInfoImage(remainingSlots, fileName, files);

                await this.loadInitialData();
                // } else {
                //     // PNG가 아닌 파일인 경우 사용자에게 알림
                //     this.alertWarning(`${files.name}은(는) PNG 형식이 아닙니다. PNG 이미지만 업로드 가능합니다.`);
                //     console.warn(`PNG가 아닌 파일 거부됨: ${files.name} (${files.type})`);
                // }

                // 파일 입력 초기화
                event.target.value = "";
            } catch (error) {
                this.alertWarning(error);
                console.error("handleFileUpload handleFileUpload func 오류:", error);

                throw error;
            }
        },

        async removeImage(index) {
            const result = await this.alertConfirmWarning("이미지를 삭제하시겠습니까?");
            if (!result.value) return;

            const image = this.images[index];
            try {
                // API에서 이미지 삭제
                await this.deleteFacilityOpInfoImage(image.position);

                // Blob URL 정리 (Base64 URL은 해제할 필요 없음)
                if (image.blob && image.url && image.url.startsWith("blob:")) {
                    URL.revokeObjectURL(image.url);
                }

                // 삭제 성공 후 이미지 목록 새로고침
                await this.loadInitialData();
            } catch (error) {
                console.error(`이미지 삭제 중 오류 (${image.name}):`, error);
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return "0 Bytes";
            const k = 1024;
            const sizes = ["Bytes", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
        },

        formatDate(dateString) {
            if (!dateString) return "";
            const date = new Date(dateString);
            return date.toLocaleString("ko-KR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        generateThumbnailName(originalName, extension, prefix) {
            if (!originalName) return "";

            // 숫자만 추출 (정규식 사용)
            const numbers = originalName.match(/\d+/g);

            // 첫 번째 숫자 사용
            const number = numbers[0];

            return `${prefix}-${number}.${extension}`;
        },

        async openImageInNewWindow(image) {
            try {
                // 원본 이미지명에서 PNG 이미지 가져오기
                const originalImageName = image.name;
                // const pngImageName = this.generateThumbnailName(originalImageName, "png", "image");

                // PNG 이미지 API 호출
                const imageResponse = await this.getOriginalImage(originalImageName);

                if (imageResponse && imageResponse.url) {
                    // 새창 열기
                    // 화면을 최대한 풀사이즈로 새 창을 엽니다
                    const newWindow = window.open(
                        "",
                        "_blank",
                        `left=0,top=0,width=${window.screen.availWidth},height=${window.screen.availHeight},scrollbars=yes`
                    );

                    if (newWindow) {
                        // 새창에 이미지 표시
                        newWindow.document.write(`
                             <!DOCTYPE html>
                             <html>
                             <head>
                                 <title>${originalImageName} - 상세보기</title>
                                 <style>
                                     body {
                                         margin: 0;
                                         padding: 20px;
                                         background: #f5f5f5;
                                         font-family: Arial, sans-serif;
                                         display: flex;
                                         flex-direction: column;
                                         align-items: center;
                                         min-height: 100vh;
                                     }
                                     .image-container {
                                         background: white;
                                         padding: 20px;
                                         border-radius: 8px;
                                         box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                                         text-align: center;
                                         max-width: 90%;
                                     }

                                     .image-display {
                                         max-width: 100%;
                                         height: auto;
                                         border-radius: 4px;
                                         box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                                     }


                                 </style>
                             </head>
                             <body>
                                 <div class="image-container">
                                     <img src="${imageResponse.url}" alt="${originalImageName}" class="image-display" />

                                 </div>
                             </body>
                             </html>
                         `);

                        newWindow.document.close();
                    } else {
                        // 팝업 차단된 경우 경고
                        this.alertWarning("팝업이 차단되었습니다. 팝업 차단을 해제해주세요.");
                    }
                } else {
                    this.alertWarning("이미지를 불러올 수 없습니다.");
                }
            } catch (error) {
                console.error("이미지 새창 열기 실패:", error);
                this.alertWarning("이미지를 열 수 없습니다.");
            }
        },
    },
};
</script>

<style scoped>
.facility-clbrtn-hist-table {
    box-shadow: rgba(0, 0, 0, 0.15) 0px 3px 3px 0px;
}

.facility-management-info {
    padding: 8px;
    background: #fff;
    border-radius: 8px;
    margin: 0 auto;
}

.info-container {
    display: flex;
    flex-direction: row;
    gap: 40px;
    align-items: flex-start;
    position: relative;
}

/* 좌측 메타데이터 섹션 */
.metadata-section {
    flex: 2;
    min-width: 0;
    position: relative;
}

/* 우측 이미지 섹션 */
.image-section {
    flex: 1;
    min-width: 0;
    max-width: 400px;
    position: relative;
}

/* 구분선 추가 */
.image-section::before {
    content: "";
    position: absolute;
    left: -20px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: linear-gradient(to bottom, transparent, #dee2e6 20%, #dee2e6 80%, transparent);
}

/* 구분선 위에 장식 요소 추가 */

.section-title {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    padding-bottom: 8px;
    position: relative;
}

.form-container {
    display: flex;
    flex-direction: column;
}

.facility-form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    align-items: end;
}

.form-group {
    display: flex;
    flex-direction: column;
}

.form-group label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.form-control {
    width: 100%;
    padding: 10px 12px;
    /* border: 2px solid #e9ecef; */
    border-radius: 6px;
    font-size: 13px;
    transition: all 0.3s ease;
    /* background: #f8f9fa; */
}

.capacity-input-group {
    display: flex;
    gap: 8px;
}

.capacity-input {
    flex: 2;
}

.unit-input {
    flex: 1;
    min-width: 70px;
}

.form-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 6px;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.update-date {
    font-size: 11px;
    color: #6c757d;
    font-style: italic;
    padding: 6px 10px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 4px;
    border: 1px solid #dee2e6;
}

/* 우측 이미지 섹션 */
.image-section {
    flex: 1;
    min-width: 0;
    max-width: 400px;
}

.image-upload-container {
    width: 100%;
}

.image-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 100%;
}

.image-item {
    display: flex;
    flex-direction: column;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
    transition: all 0.3s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    width: 100%;
}

.image-item:hover {
    border-color: #007bff;
    box-shadow: 0 4px 16px rgba(0, 123, 255, 0.15);
    transform: translateY(-2px);
}

.image-preview {
    position: relative;
    width: 100%;
    height: 170px;
    overflow: hidden;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
}

.preview-img {
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    background: #e9ecef;
}

.image-preview:hover .preview-img {
    transform: scale(1.05);
}

.image-overlay {
    position: absolute;
    top: 6px;
    right: 6px;
}

.remove-btn {
    background: rgba(220, 53, 69, 0.9);
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.remove-btn:hover {
    background: rgba(220, 53, 69, 1);
    transform: scale(1.1);
}

.image-info-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgb(233, 236, 239, 0.5);
    color: black;
    padding: 8px 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    backdrop-filter: blur(3px);
    transition: all 0.3s ease;
}

.image-preview:hover .image-info-overlay {
    background: rgba(233, 236, 239, 0.7);
}

.image-name {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #424242;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.image-size {
    display: block;
    font-size: 10px;
    color: #757575;
}

.image-upload-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 170px;
    border: 2px dashed #ced4da;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    width: 100%;
}

.image-upload-btn:hover {
    border-color: #007bff;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    transform: translateY(-1px);
}

.upload-icon {
    font-size: 20px;
    color: #6c757d;
    margin-bottom: 6px;
    transition: all 0.3s ease;
}

.image-upload-btn:hover .upload-icon {
    color: #007bff;
    transform: scale(1.1);
}

.upload-text {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 2px;
}

.upload-hint {
    font-size: 10px;
    color: #6c757d;
}

.description-textarea {
    min-height: 50px;
    max-height: 150px;
}
</style>
